import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { loadStripe } from '@stripe/stripe-js/pure';
import { environment } from '../../../environments/environment';
import { Observable, BehaviorSubject, Subject, lastValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { PaymentMethod } from '@stripe/stripe-js';
import { StudentModel } from '@generativ/much-smarter-models';

const MILLISECONDS_ONE_DAY = 86400000;

@Injectable({ providedIn: 'root' })
export class PaymentService {
  stripe: any;
  elements: any;
  lastCheck: number;
  status: { authorized: boolean; premium: boolean };
  isUk: boolean;
  isTesting: boolean;

  private userAgreemnentStatus: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  public userAgreemnentStatus$: Observable<boolean> = this.userAgreemnentStatus.asObservable();

  $openPaymentFreezeModalHandler: Subject<any> = new Subject<any>();

  constructor(
    private http: HttpClient
  ) {
    this.isTesting = ['localhost', 'dev', 'staging'].some(key => window.location.host.includes(key));
    this.isUk = this.isTesting ? localStorage.getItem('selectedCountry') === 'uk' : window.location.host.includes('co.uk');
  }

  async initStripe(): Promise<any> {
    if (this.stripe) {
      return this.stripe;
    }

    this.stripe = await loadStripe(environment.stripePublishableKey);
    this.elements = this.stripe.elements();

    return this.stripe;
  }

  checkout(stripeToken: string, nameOnCard: string = null): Observable<any> {
    if (window.location.host.includes('co.uk')) {
      return this.http.post(`${environment.msStudentApiUrl}payment/uk`, {stripeToken, nameOnCard});
    }
    return this.http.post(`${environment.msStudentApiUrl}payment/`, {stripeToken, nameOnCard});
  }

  startTrial(stripeToken: string, nameOnCard: string): Observable<StudentModel> {
    return this.http.post<StudentModel>(
      `${environment.msStudentApiUrl}payment/trial`,
      { stripeToken, nameOnCard }
    );
  }

  checkSubscriptionStatus(): Promise<{ authorized: boolean; premium: boolean }> {
    if (this.status && !this.checkStatusExpired()) {
      return Promise.resolve(this.status);
    }
    if (this.isUk) {
      return lastValueFrom(this.http.get<{ authorized: boolean; premium: boolean }>(`${environment.msStudentApiUrl}payment/uk-status`)
        .pipe(
          map(status => {
            this.status = status;
            this.lastCheck = Date.now();
            return status;
          })
        ));
    } else {
      return lastValueFrom(this.http.get<{ authorized: boolean; premium: boolean }>(`${environment.msStudentApiUrl}payment/status`)
        .pipe(
          map(status => {
            this.status = status;
            this.lastCheck = Date.now();
            return status;
          })
        ));
    }
  }

  getSubscriptionProduct(): Observable<any> {
    return this.http.get(`${environment.msStudentApiUrl}payment/`);
  }

  getBilling(): Observable<{ paymentMethod: PaymentMethod; charges: any[] }> {
    return this.http.get<{ paymentMethod: PaymentMethod; charges: any[] }>(
      `${environment.msStudentApiUrl}payment/info`
    );
  }

  updateBillingInfo(stripeToken: string): Observable<PaymentMethod> {
    return this.http.patch<PaymentMethod>(`${environment.msStudentApiUrl}payment/billing`, stripeToken);
  }

  upgradeToPremium(): Observable<void> {
    if (this.isUk) {
      return this.http.post<void>(`${environment.msStudentApiUrl}payment/uk-upgrade`, null);
    } else {
      return this.http.post<void>(`${environment.msStudentApiUrl}payment/upgrade`, null);
    }
  }

  updateUserAgreementStatus(status) {
    this.userAgreemnentStatus.next(status);
  }

  private checkStatusExpired(): boolean {
    return (Date.now() - this.lastCheck) > MILLISECONDS_ONE_DAY;
  }
}
